import React from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { OverviewReportResponse } from "@/gen/reports/overview_pb";

const OverviewCard = ({
  title,
  value,
  extra,
  color,
}: {
  title: string;
  value: string | React.ReactNode;
  extra?: string | React.ReactNode;
  color?: string;
}) => {
  return (
    <div className="border-[2px] rounded-lg py-4 px-6">
      <p className="font-medium">{title}</p>
      <p className="font-semibold text-2xl whitespace-pre">
        {value}
        <br />
        <span
          className={`text-${color ? color : "gray"}-500 text-sm font-normal`}
        >
          {extra}
        </span>
      </p>
    </div>
  );
};

const Overview = ({ data }: { data: OverviewReportResponse }) => {
  return (
    <div className="w-full grid grid-cols-4 space-x-6">
      <OverviewCard
        title="Total Sales"
        value={convertToCurrencyRounded(
          data.totalSales?.requestPeriodValue!
        )}
        color={
          data.totalSales?.percentageChange === 0
            ? "gray"
            : data.totalSales?.percentageChange! > 0
              ? "green"
              : "red"
        }
        extra={
          data.totalSales?.percentageChange === 0
            ? "No change from last period"
            : data.totalSales?.percentageChange! > 0
              ? ((data.totalSales?.requestPeriodValue! -
                  data.totalSales?.previousPeriodValue!) /
                  data.totalSales?.previousPeriodValue!) *
                100 >
                1000
                ? "100%+ from last period"
                : `+ ${data.totalSales?.percentageChange!.toFixed(2)}% from last period`
              : `- ${Math.abs(data.totalSales?.percentageChange!).toFixed(
                  2
                )}% from last period`
        }
      />
      <OverviewCard
        title="Total Discounts Given"
        value={convertToCurrencyRounded(
          data.totalDiscountsGiven?.requestPeriodValue!
        )}
        color={
          data.totalDiscountsGiven?.percentageChange === 0
            ? "gray"
            : data.totalDiscountsGiven?.percentageChange! > 0
              ? "green"
              : "red"
        }
        extra={
          data.totalDiscountsGiven?.percentageChange === 0
            ? "No change from last period"
            : data.totalDiscountsGiven?.percentageChange! > 0
              ? ((data.totalDiscountsGiven?.requestPeriodValue! -
                  data.totalDiscountsGiven?.previousPeriodValue!) /
                  data.totalDiscountsGiven?.previousPeriodValue!) *
                100 >
                1000
                ? "100%+ from last period"
                : `+ ${data.totalDiscountsGiven?.percentageChange!.toFixed(2)}% from last period`
              : `- ${Math.abs(data.totalDiscountsGiven?.percentageChange!).toFixed(
                  2
                )}% from last period`
        }
      />
      <OverviewCard
        title="Refunded Transactions"
        value={convertToCurrencyRounded(
          data.totalRefundedSales?.requestPeriodValue!
        )}
        color={
          data.totalRefundedSales?.percentageChange === 0
            ? "gray"
            : data.totalRefundedSales?.percentageChange! > 0
              ? "green"
              : "red"
        }
        extra={
          data.totalRefundedSales?.percentageChange === 0
            ? "No change from last period"
            : data.totalRefundedSales?.percentageChange! > 0
              ? ((data.totalRefundedSales?.requestPeriodValue! -
                  data.totalRefundedSales?.previousPeriodValue!) /
                  data.totalRefundedSales?.previousPeriodValue!) *
                100 >
                1000
                ? "100%+ from last period"
                : `+ ${data.totalRefundedSales?.percentageChange!.toFixed(2)}% from last period`
              : `- ${Math.abs(data.totalRefundedSales?.percentageChange!).toFixed(
                  2
                )}% from last period`
        }
      />
      <OverviewCard
        title="Next Supplier Payment"
        value={
          data.isNextSupplierPaymentDue ?
            dayjs(data.nextSupplierPaymentDueDate?.toDate())
              .format("DD MMM YYYY")
            : "Not Due"
        }
        extra={
          data.isNextSupplierPaymentDue
            ? `Due to ${data.nextSupplierPaymentSupplierName}`
            : "No payments due"
        }
      />
    </div>
  );
};

export default Overview;
